import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { BrowserRouter } from "react-router-dom";
import { PostHogProvider } from 'posthog-js/react';
import { b2cPolicies, msalConfig, protectedResources } from './config/msalConfig';
import App from './App';
import { posthog } from './lib/posthog.client';
import { markInviteSignupFromLocation } from './utils/inviteSignup';
import { compareIssuingPolicy } from './utils/claimUtils';

/**
* MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
* For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
*/
await markInviteSignupFromLocation({
    redirectPath: '/invite-complete',
    syncEndpoint: protectedResources?.api?.baseEndPoint
        ? `${protectedResources.api.baseEndPoint.replace(/\/+$/, '')}/auth/ping`
        : null
});
export const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();
// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

  // Optional - This will update account state if a user signs in from another tab or window
  msalInstance.enableAccountStorageEvents();

msalInstance.addEventCallback((event) => {    
    //console.log(event);
    // if (
    //     (event.eventType === EventType.LOGIN_SUCCESS ||
    //         event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
    //         event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    //     event.payload.account ) 
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) 
    {
        console.log("login success", event.payload.account);
        var userEmail = event.payload.account?.idTokenClaims?.email || event.payload.account.localAccountId;

        posthog.identify(event.payload.account.localAccountId, {
            email: userEmail,
            name: event.payload.account?.idTokenClaims?.name || event.payload.account.localAccountId            
        });

        // Tokens returned from the change-password / forgot-password B2C policies are not
        // valid sign-in sessions. Activating them here breaks the primary sign-in session.
        // Only activate accounts from the sign-in policy.
        const isChangeOrForgotPassword =
            compareIssuingPolicy(event.payload.account.idTokenClaims, b2cPolicies.names.changePassword) ||
            compareIssuingPolicy(event.payload.account.idTokenClaims, b2cPolicies.names.forgotPassword);

        if (!isChangeOrForgotPassword) {
            msalInstance.setActiveAccount(event.payload.account);
        }
        posthog.capture('login', { iss: event.payload.account?.idTokenClaims?.iss, acr: event.payload.account?.idTokenClaims?.acr });
    }
    if(event.eventType === EventType.LOGOUT_SUCCESS) {
        console.log("logout success", event.payload);
        posthog.reset();    
        posthog.capture('logout', { reason: event.payload });
    }
    if(event.eventType === EventType.SSO_SILENT_FAILURE) {
        console.warn("SSO silent failure:", event.error);
    }
});

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
    // <React.StrictMode>
        <PostHogProvider
            client={posthog}>
            <BrowserRouter>
                <App instance={msalInstance} />
            </BrowserRouter>
        </PostHogProvider>
    // </React.StrictMode>
);

// PATCH: Global unhandled promise rejection handler
// Catches any unhandled promise rejections that slip through try-catch blocks
// This prevents "Unknown promise rejection reason" errors in production
window.addEventListener('unhandledrejection', function(event) {
    console.error('[Global] Unhandled promise rejection detected:', event.reason);
    console.error('[Global] Promise:', event.promise);
    console.error('[Global] Stack trace:', event.reason?.stack);

    // Capture in PostHog for monitoring (with error handling)
    try {
        if (posthog && posthog.capture) {
            posthog.capture('unhandled-promise-rejection', {
                reason: String(event.reason),
                message: event.reason?.message || 'No message',
                stack: event.reason?.stack || 'No stack trace',
                location: window.location.pathname,
                timestamp: new Date().toISOString()
            });
        }
    } catch (posthogError) {
        console.error('[Global] PostHog capture failed:', posthogError);
    }

    // Prevent the default "Uncaught (in promise)" error in console
    // This stops the "Unknown promise rejection reason" message
    event.preventDefault();
});
